import { SupportConversation } from './SupportConversation';
import '../pages/contact.css';
import { useGameOverlayPause } from './useGameOverlayPause';
import { DirectMessagesList, useDirectMessages } from './DirectMessages';
import { createPortal } from 'react-dom';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Megaphone, Bell, CheckCheck, Gamepad2, Check, MailOpen, X } from 'lucide-react';
import { api, json } from '../api';
import { usePlatform } from '../store';
import { UserAvatar } from './UserAvatar';
import './notifications.css';
import { NoticeCard } from './NoticeCard';
import { lockBackgroundScroll } from './Modal';
import './mobile-messages.css';
const mobileMessagesQuery = '(max-width: 760px), (hover: none) and (pointer: coarse)';
type Notice = {
  id: string;
  seq: number;
  kind: string;
  actorId: string;
  actorName: string;
  avatarUrl: string;
  game?: {
    id: string;
    name: string;
    coverImage: string;
    players: number | null;
    capacity: number | null;
    roomLabel: string;
  };
  reference: string;
  version: string;
  title: string;
  body: string;
  status: string;
  createdAt: string;
  read: boolean;
};
type Inbox = { items: Notice[]; next: number | null; unreadCount: number; watermark: number };
const Context = createContext<ReturnType<typeof useNotificationState> | null>(null);
export function useNotificationCount() {
  return useContext(Context)?.count || 0;
}
export function NotificationsButton() {
  const { data } = usePlatform();
  const { count, refresh, setError, setConfirm } = useContext(Context)!;
  const { summary } = useDirectMessages();
  const [section, setSection] = useState<'notifications' | 'messages'>('notifications');
  const [announcementId, setAnnouncementId] = useState<string | null>(null);
  const total = count + summary.unreadCount;
  const [open, setOpen] = useState(false);
  useGameOverlayPause(open);
  const [mobile, setMobile] = useState(() => window.matchMedia(mobileMessagesQuery).matches);
  useEffect(() => {
    const media = window.matchMedia(mobileMessagesQuery);
    const update = () => setMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const trigger = useRef<HTMLButtonElement>(null),
    panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 64, left: 8, arrow: 300 });
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, data?.user?.id]);
  useEffect(() => {
    const show = (event: Event) => {
      if (!data?.user) return;
      const detail = (event as CustomEvent).detail;
      setSection(detail === 'messages' ? 'messages' : 'notifications');
      setAnnouncementId(typeof detail?.announcementId === 'string' ? detail.announcementId : null);
      window.dispatchEvent(new Event('moyu:open-notifications'));
      setOpen(true);
      setConfirm(null);
      void refresh().catch((e) => setError((e as Error).message));
    };
    window.addEventListener('moyu:show-game-messages', show);
    return () => window.removeEventListener('moyu:show-game-messages', show);
  }, [data?.user?.id, location.pathname, refresh]);
  useLayoutEffect(() => {
    if (!open || !mobile) return;
    const unlock = lockBackgroundScroll();
    const root = document.getElementById('root');
    const wasInert = root?.inert;
    if (root) root.inert = true;
    return () => {
      if (root) root.inert = !!wasInert;
      unlock();
    };
  }, [open, mobile]);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (mobile) return;
      if (r) {
        if (
          location.pathname.startsWith('/play/') &&
          window.matchMedia('(max-width: 760px), (hover: none) and (pointer: coarse)').matches
        ) {
          setPosition({
            top:
              Math.max(
                64,
                document.querySelector('.player-toolbar')?.getBoundingClientRect().bottom || 0,
              ) + 8,
            left: 8,
            arrow: 48,
          });
          return;
        }
        const width = Math.min(320, window.innerWidth - 16);
        const left = Math.max(
          8,
          Math.min(r.left + r.width / 2 - width * 0.68, window.innerWidth - width - 8),
        );
        setPosition({
          top: r.bottom + 14,
          left,
          arrow: Math.max(20, Math.min(width - 20, r.left + r.width / 2 - left)),
        });
      }
    };
    place();
    panel.current?.focus({ preventScroll: true });
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const outside = (e: PointerEvent) => {
      if (
        !panel.current?.contains(e.target as Node) &&
        !trigger.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (mobile && e.key === 'Tab') {
        const controls = Array.from(
          panel.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], [tabindex="0"]',
          ) || [],
        ).filter((el) => el.getClientRects().length);
        const first = controls[0],
          last = controls[controls.length - 1];
        if (
          first &&
          ((e.shiftKey &&
            (document.activeElement === first || document.activeElement === panel.current)) ||
            (!e.shiftKey && document.activeElement === last))
        ) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      }
      if (e.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const close = () => setOpen(false);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('moyu:open-friends', close);
    window.addEventListener('moyu:open-theme', close);
    window.addEventListener('moyu:chat-opened', close);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('moyu:open-friends', close);
      window.removeEventListener('moyu:open-theme', close);
      window.removeEventListener('moyu:chat-opened', close);
    };
  }, [open, mobile]);
  if (!data?.user) return null;
  return (
    <>
      <button
        ref={trigger}
        className="friends-trigger notification-trigger"
        title="消息"
        aria-label={`消息${total ? `，${total} 条未读` : ''}`}
        aria-expanded={open}
        aria-controls={open ? 'notification-dropdown' : undefined}
        onClick={() => {
          setOpen(!open);
          setConfirm(null);
          if (!open) {
            window.dispatchEvent(new Event('moyu:open-notifications'));
            void refresh().catch((e) => setError((e as Error).message));
          }
        }}
      >
        <Bell size={19} />
        <span>消息</span>
        {total > 0 && <i>{total > 99 ? '99+' : total}</i>}
      </button>
      {open &&
        createPortal(
          <div
            ref={panel}
            tabIndex={-1}
            role={mobile ? 'dialog' : 'region'}
            aria-modal={mobile || undefined}
            aria-label="消息中心"
            id="notification-dropdown"
            className={`notification-dropdown ${mobile ? 'message-fullscreen' : ''}`}
            style={
              {
                '--notification-arrow': `${position.arrow}px`,
                top: position.top,
                left: position.left,
                maxHeight: `min(560px, calc(100dvh - ${position.top + 12}px))`,
              } as React.CSSProperties
            }
          >
            <div className="message-hub">
              {mobile && (
                <header className="mobile-message-header">
                  <button
                    aria-label="返回原页面"
                    onClick={() => {
                      setOpen(false);
                      requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true }));
                    }}
                  >
                    <ArrowLeft size={22} />
                    <span>返回</span>
                  </button>
                  <h1>消息</h1>
                  <span className="mobile-message-header-spacer" aria-hidden="true" />
                </header>
              )}
              <nav className="message-section-tabs" aria-label="消息类型">
                <button
                  className={section === 'notifications' ? 'active' : ''}
                  onClick={() => setSection('notifications')}
                >
                  通知{count > 0 && <b>{count}</b>}
                </button>
                <button
                  className={section === 'messages' ? 'active' : ''}
                  onClick={() => setSection('messages')}
                >
                  私信{summary.unreadCount > 0 && <b>{summary.unreadCount}</b>}
                </button>
              </nav>
              {section === 'notifications' ? (
                <NotificationsList announcementId={announcementId} />
              ) : (
                <DirectMessagesList />
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
const labels: Record<string, string> = {
  accepted: '已接受',
  declined: '已婉拒',
  revoked: '已撤回',
  closed: '房间已关闭',
  expired: '已过期',
  removed: '已撤回',
  unavailable: '已失效',
};
function useNotificationState() {
  const { data } = usePlatform();
  const userId = data?.user?.id;
  const navigate = useNavigate(),
    location = useLocation();
  const [onlyUnread, setOnlyUnread] = useState(false),
    [inbox, setInbox] = useState<Inbox | null>(null),
    [count, setCount] = useState(0),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [confirm, setConfirm] = useState<Notice | null>(null);
  const visibleItems = useRef(20),
    latestRequest = useRef(0);
  const generation = useRef(0),
    currentUser = useRef(userId);
  currentUser.current = userId;
  const refresh = useCallback(
    async (before?: number) => {
      if (!userId) return;
      const epoch = generation.current,
        requestId = ++latestRequest.current;
      const result = await api<Inbox>(
        `/notifications?unread=${onlyUnread ? '1' : '0'}${before ? `&before=${before}` : ''}`,
      );
      if (!before) {
        while (result.next && result.items.length < visibleItems.current) {
          if (epoch !== generation.current || requestId !== latestRequest.current) return;
          const older = await api<Inbox>(
            `/notifications?unread=${onlyUnread ? '1' : '0'}&before=${result.next}`,
          );
          result.items.push(...older.items);
          result.next = older.next;
        }
      }
      if (
        epoch !== generation.current ||
        currentUser.current !== userId ||
        requestId !== latestRequest.current
      )
        return;
      if (before) visibleItems.current += result.items.length;
      setCount(result.unreadCount);
      setInbox((old) =>
        before && old
          ? {
              ...result,
              items: [
                ...old.items,
                ...result.items.filter((n) => !old.items.some((x) => x.id === n.id)),
              ],
            }
          : result,
      );
    },
    [userId, onlyUnread],
  );
  useEffect(() => {
    generation.current++;
    visibleItems.current = 20;
    setInbox(null);
    setCount(0);
    setError('');
    setBusy(false);
    setConfirm(null);
  }, [userId]);
  useEffect(() => {
    generation.current++;
    visibleItems.current = 20;
    setInbox(null);
    if (!userId) return;
    let disposed = false,
      pending = false;
    const read = async () => {
      if (document.hidden || pending) return;
      pending = true;
      try {
        await refresh();
        if (!disposed) setError('');
      } catch (e) {
        if (!disposed) setError((e as Error).message);
      } finally {
        pending = false;
      }
    };
    void read();
    const timer = setInterval(() => void read(), 5000);
    window.addEventListener('focus', read);
    window.addEventListener('moyu:notifications-refresh', read);
    return () => {
      disposed = true;
      generation.current++;
      clearInterval(timer);
      window.removeEventListener('focus', read);
      window.removeEventListener('moyu:notifications-refresh', read);
    };
  }, [refresh]);
  const run = async (task: () => Promise<unknown>) => {
    if (busy) return;
    const owner = userId;
    setBusy(true);
    setError('');
    try {
      await task();
      if (currentUser.current === owner) {
        await refresh();
        window.dispatchEvent(new Event('moyu:social-changed'));
      }
    } catch (e) {
      if (currentUser.current === owner) {
        setError((e as Error).message);
        void refresh().catch(() => {});
      }
    } finally {
      if (currentUser.current === owner) setBusy(false);
    }
  };
  const respond = (n: Notice, accept: boolean, confirmed = false) => {
    if (
      accept &&
      ['game_invite', 'party_invite'].includes(n.kind) &&
      location.pathname.startsWith('/play/') &&
      !confirmed
    ) {
      setConfirm(n);
      return;
    }
    setConfirm(null);
    void run(async () => {
      const owner = userId;
      if (n.kind === 'friend_request')
        await api(
          `/social/requests/${encodeURIComponent(n.actorId)}`,
          json('POST', { accept, version: n.version }),
        );
      else {
        const result = await api<{ gameId: string; roomId: string }>(
          n.kind === 'party_invite'
            ? `/multiplayer/party/invites/${n.reference}/respond`
            : `/social/invites/${n.reference}/respond`,
          json('POST', { accept }),
        );
        if (accept && currentUser.current === owner) {
          navigate(
            n.kind === 'party_invite'
              ? `/play/${result.gameId}`
              : `/play/${result.gameId}?room=${result.roomId}`,
          );
          if (n.kind === 'party_invite') window.dispatchEvent(new Event('moyu:party-accepted'));
        }
      }
    });
  };
  return {
    count,
    confirm,
    setConfirm,
    onlyUnread,
    setOnlyUnread,
    inbox,
    error,
    busy,
    run,
    respond,
    refresh,
    setBusy,
    setError,
  };
}
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const value = useNotificationState();
  const { data } = usePlatform();
  return (
    <Context.Provider value={value}>
      {children}
      {data?.user && <InvitationAlert key={data.user.id} userId={data.user.id} />}
    </Context.Provider>
  );
}
function InvitationAlert({ userId }: { userId: string }) {
  const { inbox, busy, error, respond, confirm, setConfirm } = useContext(Context)!;
  const location = useLocation();
  const storageKey = `moyu:notice-dismissed:${userId}`;
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(saved) ? saved.filter((id): id is string => typeof id === 'string') : [];
    } catch {
      return [];
    }
  });
  const candidates =
    inbox?.items.filter(
      (n) =>
        !n.read &&
        !dismissed.includes(n.id) &&
        ((['game_invite', 'party_invite', 'friend_request'].includes(n.kind) &&
          n.status === 'pending') ||
          (n.kind === 'announcement' &&
            !location.pathname.startsWith('/play/') &&
            !location.pathname.startsWith('/admin'))),
    ) || [];
  // Invitations are actionable and take precedence over announcements.
  const n = candidates.find((n) => n.kind !== 'announcement') || candidates[0];
  const dismiss = () => {
    if (!n) return;
    const next = [...dismissed, n.id].slice(-500);
    setDismissed(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* optional persistence */
    }
    if (confirm?.id === n.id) setConfirm(null);
  };
  if (!n) return null;
  const announcement = n.kind === 'announcement',
    friend = n.kind === 'friend_request';
  const joining = confirm?.id === n.id;
  return createPortal(
    <NoticeCard
      className="notice-social"
      name={n.actorName}
      avatar={n.avatarUrl}
      announcement={announcement}
      meta={`${announcement ? '平台公告' : friend ? '好友申请' : '游戏邀请'} · ${notificationTime(n.createdAt)}`}
      onClose={dismiss}
      actions={
        announcement ? (
          <>
            <button onClick={dismiss}>稍后再看</button>
            <button
              className="notice-primary"
              onClick={() => {
                dismiss();
                window.dispatchEvent(
                  new CustomEvent('moyu:show-game-messages', { detail: { announcementId: n.id } }),
                );
              }}
            >
              查看公告
            </button>
          </>
        ) : (
          <>
            <button
              disabled={busy}
              onClick={() => (joining ? setConfirm(null) : respond(n, false))}
            >
              {joining ? '留在当前游戏' : friend ? '暂不添加' : '暂不加入'}
            </button>
            <button
              className="notice-primary"
              disabled={busy}
              onClick={() => respond(n, true, joining)}
            >
              {joining
                ? '确认加入'
                : friend
                  ? '添加好友'
                  : n.kind === 'party_invite'
                    ? '加入队伍'
                    : '加入游戏'}
            </button>
          </>
        )
      }
    >
      <h3>{announcement ? n.title : friend ? '请求添加你为好友' : '邀请你一起玩'}</h3>
      {announcement && <p className="notice-excerpt">{n.body}</p>}
      {friend && <p className="notice-excerpt">一起上岛，发现更多好玩</p>}
      {n.game && (
        <div className="notification-game">
          <div className="notification-game-cover">
            <Gamepad2 size={22} />
            {n.game.coverImage && (
              <img
                src={n.game.coverImage}
                alt=""
                onError={(e) => {
                  e.currentTarget.hidden = true;
                }}
              />
            )}
          </div>
          <div>
            <strong>{n.game.name}</strong>
            <small>
              {n.game.roomLabel}
              {n.game.players !== null && n.game.capacity !== null
                ? ` · ${n.game.players}/${n.game.capacity} 人`
                : ''}
            </small>
          </div>
        </div>
      )}
      {joining && <p className="invitation-alert-warning">将离开当前游戏，请确认进度已保存。</p>}
      {error && (
        <p className="invitation-alert-error" role="alert">
          {error}
        </p>
      )}
    </NoticeCard>,
    document.body,
  );
}
function NotificationsList({ announcementId }: { announcementId: string | null }) {
  const [supportId, setSupportId] = useState<string | null>(null);
  const { data } = usePlatform();
  const {
    count,
    onlyUnread,
    setOnlyUnread,
    inbox,
    error,
    busy,
    run,
    respond,
    refresh,
    setBusy,
    setError,
    confirm,
    setConfirm,
  } = useContext(Context)!;
  const [expanded, setExpanded] = useState<string | null>(announcementId);
  useEffect(() => {
    setExpanded(announcementId);
  }, [announcementId]);
  if (!data?.user) return null;
  if (supportId)
    return (
      <section className="notifications-panel support-notification-detail">
        <button
          className="notification-text"
          onClick={() => {
            setSupportId(null);
            void refresh();
          }}
        >
          ← 返回消息
        </button>
        <SupportConversation id={supportId} />
      </section>
    );
  return (
    <section className="notifications-panel" aria-label="消息列表">
      <div className="notifications-summary">
        <h2>消息{count > 0 && <span className="notification-count">{count}</span>}</h2>
        <button
          className="notification-read-all"
          disabled={busy || !inbox || !count}
          onClick={() =>
            void run(() =>
              api('/notifications/read-all', json('POST', { through: inbox!.watermark })),
            )
          }
        >
          <CheckCheck size={16} />
          全部已读
        </button>
      </div>
      <nav className="notifications-tabs" aria-label="通知筛选">
        <button className={!onlyUnread ? 'active' : ''} onClick={() => setOnlyUnread(false)}>
          全部
        </button>
        <button className={onlyUnread ? 'active' : ''} onClick={() => setOnlyUnread(true)}>
          未读{count > 0 && <b>{count}</b>}
        </button>
      </nav>
      {error && (
        <div className="notifications-error" role="alert">
          {error}
          <button onClick={() => void run(() => refresh())}>重试</button>
        </div>
      )}
      {confirm && (
        <div className="notifications-error">
          <strong>前往好友的房间？</strong>
          <p>这会离开当前游戏，请先确认进度已保存。</p>
          <button className="button" disabled={busy} onClick={() => respond(confirm, true, true)}>
            确认加入
          </button>
          <button className="notification-text" onClick={() => setConfirm(null)}>
            留在当前游戏
          </button>
        </div>
      )}
      <div className="notifications-list" role="list" aria-label="通知记录" aria-busy={busy}>
        {!inbox && !error && <div className="notifications-empty">正在收取消息…</div>}
        {inbox && !inbox.items.length && (
          <div className="notifications-empty">
            <MailOpen size={38} />
            <strong>{onlyUnread ? '没有未读通知' : '暂时没有新消息'}</strong>
            <p>平台公告、好友申请和游戏邀请会出现在这里</p>
          </div>
        )}
        {inbox?.items.map((n) => (
          <article
            role="listitem"
            key={n.id}
            className={`notification-item ${n.read ? '' : 'unread'}`}
          >
            <>
              {['announcement', 'support_reply'].includes(n.kind) ? (
                <span className="announcement-icon">
                  <Megaphone size={23} />
                </span>
              ) : (
                <UserAvatar src={n.avatarUrl} size={48} />
              )}
            </>
            <div className="notification-content">
              <div className="notification-title">
                {!n.read && <span aria-label="未读" className="notification-dot" />}
                <strong>{n.actorName}</strong>
                <time dateTime={n.createdAt}>{notificationTime(n.createdAt)}</time>
              </div>
              {n.kind === 'announcement' && <h3 className="announcement-title">{n.title}</h3>}
              <p
                className={
                  n.kind === 'announcement'
                    ? `announcement-body ${expanded === n.id ? 'expanded' : ''}`
                    : undefined
                }
              >
                {['game_invite', 'party_invite'].includes(n.kind)
                  ? '邀请你一起玩'
                  : n.kind === 'friend_request'
                    ? '想和你成为好友'
                    : n.kind === 'friend_accepted'
                      ? '已接受你的好友申请'
                      : n.kind === 'game_invite_accepted'
                        ? '已接受你的游戏邀请'
                        : n.body}
              </p>
              {n.game && (
                <div className="notification-game">
                  <div className="notification-game-cover">
                    {n.game.coverImage ? (
                      <img
                        src={n.game.coverImage}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.hidden = true;
                        }}
                      />
                    ) : null}
                    <Gamepad2 size={25} />
                  </div>
                  <div>
                    <strong>{n.game.name}</strong>
                    <small>
                      {n.game.players !== null && n.game.capacity !== null
                        ? `${n.game.roomLabel} · ${n.game.players}/${n.game.capacity} 人`
                        : '房间已结束'}
                    </small>
                  </div>
                </div>
              )}
              <div className="notification-actions">
                {n.kind === 'support_reply' && (
                  <button className="button secondary" onClick={() => setSupportId(n.reference)}>
                    查看并回复
                  </button>
                )}
                {n.kind === 'announcement' && (
                  <button
                    className="notification-text"
                    disabled={busy}
                    onClick={() => {
                      setExpanded(expanded === n.id ? null : n.id);
                    }}
                  >
                    {expanded === n.id ? '收起公告' : '阅读全文'}
                  </button>
                )}
                {n.status === 'pending' ? (
                  <>
                    <button
                      className={`button notification-accept ${['game_invite', 'party_invite'].includes(n.kind) ? 'game' : 'friend'}`}
                      disabled={busy}
                      onClick={() => respond(n, true)}
                    >
                      {['game_invite', 'party_invite'].includes(n.kind)
                        ? n.kind === 'party_invite'
                          ? '加入队伍'
                          : '加入游戏'
                        : '接受'}
                    </button>
                    <button
                      className="notification-text"
                      disabled={busy}
                      onClick={() => respond(n, false)}
                    >
                      {['game_invite', 'party_invite'].includes(n.kind) ? '婉拒' : '忽略'}
                    </button>
                  </>
                ) : (
                  labels[n.status] && (
                    <span className="notification-status">{labels[n.status]}</span>
                  )
                )}
                {!n.read && (
                  <button
                    className="notification-mark"
                    title="标为已读"
                    aria-label={`将 ${n.actorName} 的消息标为已读`}
                    disabled={busy}
                    onClick={() =>
                      void run(() => api(`/notifications/${n.id}/read`, json('POST', {})))
                    }
                  >
                    <Check size={15} />
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
        {inbox?.next && (
          <button
            className="notification-more"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void refresh(inbox.next!)
                .catch((e) => setError((e as Error).message))
                .finally(() => setBusy(false));
            }}
          >
            加载更早的消息
          </button>
        )}
        {inbox && !inbox.next && inbox.items.length > 0 && (
          <div className="notification-end">没有更多消息了</div>
        )}
      </div>
    </section>
  );
}

function notificationTime(value: string) {
  const date = new Date(value),
    now = new Date(),
    elapsed = Math.max(0, now.getTime() - date.getTime());
  if (elapsed < 60000) return '刚刚';
  if (elapsed < 3600000) return `${Math.floor(elapsed / 60000)}分钟前`;
  if (date.toDateString() === now.toDateString())
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return date.toDateString() === yesterday.toDateString()
    ? '昨天'
    : date.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });
}
